'use client'

import { AlertCircle, File as FileIcon, FileText, Image as ImageIcon, Loader2, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '../cn'

interface BasePersisted {
  id: string
  fileName: string
  fileUrl: string
  fileSize: number
  contentType: string
}

interface PendingProps {
  variant: 'pending'
  file: File
  progress: number
  error?: string
  onRemove?: () => void
}

interface PersistedProps {
  variant: 'persisted'
  attachment: BasePersisted
  /** When set, clicking the chip opens a lightbox instead of navigating. */
  onOpenImage?: (url: string) => void
}

export type AttachmentChipProps = PendingProps | PersistedProps

/**
 * Map an upload error message to a short user hint. Specific patterns
 * win; the fallback intentionally avoids "sign out" guidance since
 * upload failures are almost never an auth problem.
 */
function hintForUploadError(message: string): string {
  if (/likely bucket CORS|reaching S3/i.test(message)) {
    return "We can't reach file storage. Try again in a minute."
  }
  if (/etag|multipart/i.test(message)) {
    return "Upload didn't finish — try again."
  }
  if (/network|fetch failed|abort/i.test(message)) {
    return 'Network looks shaky. Try again.'
  }
  return 'Something went wrong. Try again.'
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function FileTypeIcon({ contentType }: { contentType: string }) {
  if (contentType.startsWith('image/')) {
    return <ImageIcon className="h-4 w-4" />
  }
  if (contentType === 'application/pdf') {
    return <FileText className="h-4 w-4" />
  }
  return <FileIcon className="h-4 w-4" />
}

/**
 * AttachmentChip — small file pill rendered in two contexts:
 *   - composer (`variant: 'pending'`): shows local File while uploading
 *     (thumbnail from URL.createObjectURL), inline progress bar, ✕ to cancel
 *   - persisted message (`variant: 'persisted'`): server-resolved attachment
 *     with a signed GET URL — image lightbox or download link.
 *
 * Lifted from `apps/agent/client/src/components/chat/attachment-chip.tsx`.
 */
function AttachmentChip(props: AttachmentChipProps) {
  // Build a preview URL for image files in the pending case.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  useEffect(() => {
    if (props.variant !== 'pending') {
      return
    }
    if (!props.file.type.startsWith('image/')) {
      return
    }
    const url = URL.createObjectURL(props.file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [props])

  if (props.variant === 'pending') {
    const isError = !!props.error
    const isUploading = !isError && props.progress < 100
    if (isError) {
      // Single compact chip with the hint inline beneath the size — no
      // second full-width error block (the doubled-up surface was
      // overwhelming on mobile). Per
      // `apps/agent/plans/plan-action-cards-polish.md` addendum.
      return (
        <div className="flex max-w-full items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-xs">
          <div className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded bg-muted">
            {previewUrl ? (
              <img src={previewUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <FileTypeIcon contentType={props.file.type} />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate font-medium">{props.file.name}</span>
            <span className="text-muted-foreground">{formatBytes(props.file.size)}</span>
            <span className="flex items-start gap-1 text-destructive">
              <AlertCircle className="mt-px h-3 w-3 shrink-0" aria-hidden />
              <span>{hintForUploadError(props.error ?? '')}</span>
            </span>
          </div>
          {props.onRemove ? (
            <button
              type="button"
              onClick={props.onRemove}
              className="grid h-5 w-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Remove">
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      )
    }
    return (
      <div
        className={cn(
          'group flex max-w-full items-start gap-2 rounded-md border bg-background px-2 py-1.5 text-xs',
          'w-[260px] max-w-full',
        )}>
        <div className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded bg-muted">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <FileTypeIcon contentType={props.file.type} />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium">{props.file.name}</span>
          {isUploading ? (
            <div className="flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-indigo-500 transition-all"
                  style={{ width: `${Math.max(2, props.progress)}%` }}
                />
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground">{formatBytes(props.file.size)}</span>
          )}
        </div>
        {props.onRemove ? (
          <button
            type="button"
            onClick={props.onRemove}
            className="grid h-5 w-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Remove">
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>
    )
  }

  // Persisted variant
  const { attachment, onOpenImage } = props
  const isImage = attachment.contentType.startsWith('image/')

  if (isImage) {
    return (
      <button
        type="button"
        onClick={() => onOpenImage?.(attachment.fileUrl)}
        className="block overflow-hidden rounded-md border bg-background hover:opacity-90"
        aria-label={attachment.fileName}>
        <img src={attachment.fileUrl} alt={attachment.fileName} className="block max-h-64 max-w-[280px] object-cover" />
      </button>
    )
  }

  return (
    <a
      href={attachment.fileUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="typo-meta inline-flex max-w-xs items-center gap-2 rounded-md border bg-background px-2 py-1.5 hover:bg-muted">
      <div className="grid h-7 w-7 shrink-0 place-items-center rounded bg-muted text-muted-foreground">
        <FileTypeIcon contentType={attachment.contentType} />
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-medium">{attachment.fileName}</span>
        <span className="typo-caption">{formatBytes(attachment.fileSize)}</span>
      </div>
    </a>
  )
}

export { AttachmentChip }
